import { nextTick, onBeforeUnmount, onMounted, ref, type Ref } from "vue";

export function useChatKeyboardViewport(
  panel: Ref<HTMLElement | null>,
  messages: Ref<HTMLElement | null>,
) {
  const isInputFocused = ref(false);
  const panelStyle = ref<Record<string, string>>({});
  let scrollFrame = 0;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;

  const scrollToLatest = async () => {
    await nextTick();
    if (messages.value) messages.value.scrollTop = messages.value.scrollHeight;
    cancelAnimationFrame(scrollFrame);
    scrollFrame = requestAnimationFrame(() => {
      if (messages.value) messages.value.scrollTop = messages.value.scrollHeight;
    });
  };

  const updateVisibleHeight = () => {
    if (!isInputFocused.value || window.innerWidth > 768 || !panel.value) return;

    const viewport = window.visualViewport;
    const visibleBottom = viewport
      ? viewport.offsetTop + viewport.height
      : window.innerHeight;
    const availableHeight = Math.max(0, visibleBottom - panel.value.getBoundingClientRect().top);
    const parentHeight = panel.value.parentElement?.clientHeight ?? availableHeight;
    panelStyle.value = { height: `${Math.min(parentHeight, availableHeight)}px` };
    void scrollToLatest();
  };

  const onInputFocus = () => {
    if (window.innerWidth > 768) return;
    isInputFocused.value = true;
    updateVisibleHeight();
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(updateVisibleHeight, 350);
  };

  const onInputBlur = () => {
    isInputFocused.value = false;
    panelStyle.value = {};
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = null;
  };

  onMounted(() => {
    window.visualViewport?.addEventListener("resize", updateVisibleHeight);
    window.visualViewport?.addEventListener("scroll", updateVisibleHeight);
    window.addEventListener("resize", updateVisibleHeight);
  });

  onBeforeUnmount(() => {
    window.visualViewport?.removeEventListener("resize", updateVisibleHeight);
    window.visualViewport?.removeEventListener("scroll", updateVisibleHeight);
    window.removeEventListener("resize", updateVisibleHeight);
    if (settleTimer) clearTimeout(settleTimer);
    cancelAnimationFrame(scrollFrame);
  });

  return { isInputFocused, panelStyle, onInputFocus, onInputBlur, scrollToLatest };
}
